import { useEffect, useState } from 'react';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';

import useWaveSurfer from '@frontend/features/common/hooks/useWaveSurfer';
import { downloadFile } from '../../queries';

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

interface AudioWaveProps {
  fileId?: number;
}

const AudioWave = ({ fileId }: AudioWaveProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [audioSource, setAudioSource] = useState<string>();
  const { waveformRef, isPlaying, currentTime, togglePlay } = useWaveSurfer(audioSource);

  useEffect(() => {
    setIsLoading(true);
    if (fileId) {
      downloadFile(fileId)
        .then((url: any) => {
          if (!url) return;

          setAudioSource(url);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [fileId]);

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 2, height: 60 }} />;
  }

  if (!audioSource) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: (theme) => theme.palette.background.paper,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        p: 0.5,
        minWidth: 200,
        maxWidth: 400,
        boxSizing: 'border-box',
      }}
    >
      <IconButton
        onClick={togglePlay}
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.common.white,
          width: 30,
          height: 30,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.dark,
          },
        }}
      >
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>

      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <Box
          ref={waveformRef}
          id="waveform"
          sx={{
            flexGrow: 1,
            mx: 2,
            '&': {
              cursor: 'pointer',
            },
          }}
        />

        <Typography variant="caption">{formatTime(currentTime)}</Typography>
      </Box>
    </Box>
  );
};

export default AudioWave;
